import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { Operaciones } from '../../../api/operaciones';
import type { CampoCatalogo, EtiquetaCatalogo } from '../../../api/ops/clientesCatalogo';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Boton } from '../../../componentes/Boton';
import { Chip } from '../../../componentes/Chip';
import { Deshacer } from '../../../componentes/Deshacer';
import { EstadoCargando, EstadoError, EstadoSinPermiso } from '../../../componentes/EstadoPagina';
import { Filtros } from '../../../componentes/Filtros';
import { Icono } from '../../../componentes/Icono';
import { rolesDe } from '../../../logica/acceso';
import { useEnvioRetrasado } from '../../../logica/envioRetrasado';
import { estadoDeError } from '../../../logica/estados';
import { diaMes } from '../../../logica/fechasCortas';
import { useCabecera, type AccionCabecera } from '../../../marco/cabecera';
import { usePermisos } from '../../../permisos/permisos';
import { textoDeError } from '../../equipo/textos';
import type { PropsPantalla } from '../../tipos';
import { PanelCampo, PanelEtiqueta, type Abierto } from './Paneles';
import { AYUDA, PERMISO_CATALOGO, TEXTOS, TIPO, VACIA, clientesDe } from './textos';
import '../../../componentes/apartado.css';
import './catalogo.css';

type Vista = 'etiquetas' | 'campos';

interface Catalogo {
    etiquetas: EtiquetaCatalogo[];
    campos: CampoCatalogo[];
}

/**
 * Etiquetas y campos propios (2.8): filtros Etiquetas · Campos propios sin cifras; la única cifra es el uso de cada
 * etiqueta (`customer_count`, G140). Crear y editar en RV Panel; archivar al momento con «Deshacer» 5 s (la petición sale
 * al acabar, G159); «Archivadas» plegadas, sin cifra, con «Recuperar» (G152). Nada se borra (G167). Solo con
 * `clientes.catalogo.gestionar` (propietario y administrador).
 */
export function Catalogo(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, membresia, nombreNegocio, navegar } = props;
    const permisos = usePermisos();
    const avisar = useAvisoBreve();
    const gestionar = permisos.puede(PERMISO_CATALOGO);
    const editar = gestionar && !soloLectura;

    const [vista, setVista] = useState<Vista>('etiquetas');
    const [datos, setDatos] = useState<Catalogo | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [abierto, setAbierto] = useState<Abierto | null>(null);
    const [plegadas, setPlegadas] = useState(true);
    const [hecho, setHecho] = useState<string | null>(null);
    const envio = useEnvioRetrasado();
    /** `ETag` de cada etiqueta o campo que dio la última respuesta en esta sesión (para `If-Match`). */
    const [versiones] = useState(() => new Map<string, string>());

    const recargar = useCallback(() => {
        setIntento((i) => i + 1);
    }, []);
    const apuntar = useCallback((id: string, version: string | null) => {
        if (version !== null) {
            versiones.set(id, version);
        }
    }, [versiones]);

    useEffect(() => {
        if (!gestionar) {
            return;
        }
        let vivo = true;
        Promise.all([todasLasEtiquetas(ops, negocio), ops.listCustomFields(negocio, { include_archived: true })])
            .then(([t, c]) => {
                if (vivo) {
                    setDatos({ etiquetas: t, campos: c.data });
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, intento, gestionar]);

    const acciones: AccionCabecera[] = [];
    if (gestionar) {
        acciones.push(
            vista === 'etiquetas'
                ? { clave: 'crear-etiqueta', texto: 'Crear etiqueta', icono: 'plus', desactivado: soloLectura, onClick: () => { setHecho(null); setAbierto({ tipo: 'etiqueta', etiqueta: null }); } }
                : { clave: 'crear-campo', texto: 'Crear campo', icono: 'plus', desactivado: soloLectura, onClick: () => { setHecho(null); setAbierto({ tipo: 'campo', campo: null }); } },
        );
    }
    useCabecera({ ayuda: gestionar ? AYUDA[vista] : [], acciones });

    if (!gestionar) {
        return <EstadoSinPermiso seccion="Etiquetas y campos" rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;
    }
    if (error !== null && datos === null) {
        return <EstadoError seccion="las etiquetas y los campos" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={recargar} movil={movil} />;
    }
    if (datos === null) {
        return <EstadoCargando seccion="etiquetas y campos" />;
    }

    const archivar = (tipo: 'etiqueta' | 'campo', id: string, nombre: string) => {
        setAbierto(null);
        setHecho(null);
        envio.programar({
            texto: tipo === 'etiqueta' ? 'Archivada' : 'Archivado',
            donde: id,
            enviar: () => {
                (tipo === 'etiqueta' ? ops.archiveTag(negocio, id) : ops.archiveCustomField(negocio, id))
                    .then((r) => {
                        apuntar(id, r.version);
                        recargar();
                    })
                    .catch((e: unknown) => {
                        avisar({ tipo: 'error', texto: textoDeError(e, `No he podido archivar «${nombre}». Prueba de nuevo.`) });
                    });
            },
        });
    };

    const recuperar = (tipo: 'etiqueta' | 'campo', id: string, nombre: string) => {
        (tipo === 'etiqueta' ? ops.unarchiveTag(negocio, id) : ops.unarchiveCustomField(negocio, id))
            .then((r) => {
                apuntar(id, r.version);
                setHecho(tipo === 'etiqueta' ? `«${nombre}» vuelve a ofrecerse en las fichas. Quien la tenía la sigue teniendo.` : `«${nombre}» vuelve a pedirse en las fichas, con los valores que ya tenía.`);
                recargar();
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, `No he podido recuperar «${nombre}». Prueba de nuevo.`) });
            });
    };

    const activas = datos.etiquetas.filter((t) => t.archived_at == null);
    const etiquetasArchivadas = datos.etiquetas.filter((t) => t.archived_at != null);
    const activos = datos.campos.filter((c) => c.archived_at == null).sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    const camposArchivados = datos.campos.filter((c) => c.archived_at != null);
    const archivadas = vista === 'etiquetas' ? etiquetasArchivadas : camposArchivados;

    const fila = (id: string, nombre: string, dibujo: ReactNode, linea: string, extra: ReactNode, abrir: () => void, archivo: () => void, archivadoTexto: string) => {
        const pendiente = envio.pendiente?.donde === id;
        return (
            <div key={id} role="listitem" className={`rv-ca-fila-caja${pendiente ? ' rv-ca-fila-caja--apagada' : ''}`}>
                <div
                    className={`rv-ca-fila rv-ca-fila--abre rv-cat-fila${vista === 'campos' && !movil ? ' rv-cat-fila--campos' : ''}`}
                    tabIndex={pendiente ? -1 : 0}
                    aria-label={`${nombre}. ${editar ? 'Editar' : 'Ver'}`}
                    onClick={() => {
                        if (!pendiente) {
                            abrir();
                        }
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !pendiente) {
                            abrir();
                        }
                    }}
                >
                    <span className="rv-cat-nombre">
                        {dibujo}
                        <span className="rv-ca-fila__texto">
                            <strong>{nombre}</strong>
                            <span className={vista === 'campos' ? 'rv-cat-clave' : undefined}>{linea}</span>
                        </span>
                    </span>
                    {!movil && extra}
                    <span className="rv-cat-acciones">
                        {pendiente && <Chip estado="neutral">{archivadoTexto}</Chip>}
                        {editar && !pendiente && (
                            <button
                                type="button"
                                className="rv-bi rv-ca-fantasma"
                                aria-label={`Archivar ${nombre}`}
                                title={`Archivar ${nombre}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    archivo();
                                }}
                            >
                                <Icono nombre="archive" />
                            </button>
                        )}
                    </span>
                    <Icono nombre="chevron-right" className="rv-ca-chevron" />
                </div>
                {pendiente && envio.pendiente !== null && (
                    <div className="rv-ca-deshacer">
                        <Deshacer onDeshacer={envio.deshacer} clave={envio.pendiente.clave} duracion={envio.duracion} movil={movil} />
                    </div>
                )}
            </div>
        );
    };

    const filas =
        vista === 'etiquetas'
            ? activas.map((t) =>
                  fila(
                      t.id,
                      t.name,
                      <span className="rv-cat-punto" style={{ background: t.color ?? 'var(--rv-ink)' }} aria-hidden="true" />,
                      clientesDe(t.customer_count),
                      null,
                      () => { setAbierto({ tipo: 'etiqueta', etiqueta: t }); },
                      () => { archivar('etiqueta', t.id, t.name); },
                      'Archivada',
                  ),
              )
            : activos.map((c) =>
                  fila(
                      c.id,
                      c.label,
                      <span className="rv-ca-baldosa rv-cat-tipo" aria-hidden="true">
                          <Icono nombre={TIPO[c.type].icono} />
                      </span>,
                      movil ? [TIPO[c.type].nombre, (c.options ?? []).length > 0 ? `${String((c.options ?? []).length)} opciones` : '', c.required ? 'Obligatorio' : ''].filter((x) => x !== '').join(' · ') : c.key,
                      <>
                          <span className="rv-cat-celda">{TIPO[c.type].nombre}</span>
                          <span className="rv-cat-celda">{(c.options ?? []).length > 0 ? (c.options ?? []).join(', ') : '—'}</span>
                          <span className={`rv-cat-celda rv-cat-alta${c.required ? ' rv-cat-alta--si' : ''}`}>
                              <Icono nombre={c.required ? 'check' : 'minus'} />
                              {c.required ? 'Obligatorio' : 'Opcional'}
                          </span>
                      </>,
                      () => { setAbierto({ tipo: 'campo', campo: c }); },
                      () => { archivar('campo', c.id, c.label); },
                      'Archivado',
                  ),
              );

    return (
        <div className="rv-ca-apartado">
            <Filtros
                etiqueta="Etiquetas o campos propios"
                items={[{ etiqueta: 'Etiquetas' }, { etiqueta: 'Campos propios' }]}
                valor={vista === 'etiquetas' ? 0 : 1}
                onCambio={(i) => {
                    setVista(i === 0 ? 'etiquetas' : 'campos');
                    setHecho(null);
                    setPlegadas(true);
                }}
            />
            {hecho !== null && (
                <div className="rv-ca-hecho" role="status">
                    <span className="rv-ca-hecho__baldosa">
                        <Icono nombre="check" />
                    </span>
                    <span>{hecho}</span>
                </div>
            )}
            {filas.length === 0 ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa">
                        <Icono nombre="inbox" />
                    </span>
                    <strong>{VACIA[vista].titulo}</strong>
                    <span>{VACIA[vista].texto}</span>
                    {editar && (
                        <Boton tipo="secundario" icono="plus" movil={movil} className="rv-cat-vacia-b" onClick={() => { setAbierto(vista === 'etiquetas' ? { tipo: 'etiqueta', etiqueta: null } : { tipo: 'campo', campo: null }); }}>
                            {vista === 'etiquetas' ? 'Crear etiqueta' : 'Crear campo'}
                        </Boton>
                    )}
                </div>
            ) : (
                <div className="rv-ca-lista" role="list" aria-label={vista === 'etiquetas' ? 'Etiquetas' : 'Campos propios'}>
                    {vista === 'campos' && !movil && (
                        <div className="rv-cat-cab" aria-hidden="true">
                            <span>Campo</span>
                            <span>Tipo</span>
                            <span>Opciones</span>
                            <span>En el alta</span>
                        </div>
                    )}
                    {filas}
                </div>
            )}
            {soloLectura && (
                <p className="rv-ca-nota">
                    <Icono nombre="eye" />
                    <span>{TEXTOS.soloLectura}</span>
                </p>
            )}
            {archivadas.length > 0 && (
                <div className="rv-cat-archivadas">
                    <button type="button" className="rv-cat-archivadas__b" aria-expanded={!plegadas} onClick={() => { setPlegadas((p) => !p); }}>
                        <Icono nombre="archive" />
                        <span>{vista === 'etiquetas' ? 'Archivadas' : 'Archivados'}</span>
                        <Icono nombre="chevron-down" className={plegadas ? undefined : 'rv-cat-girada'} />
                    </button>
                    {!plegadas && (
                        <>
                            <div className="rv-ca-lista" role="list" aria-label={vista === 'etiquetas' ? 'Etiquetas archivadas' : 'Campos archivados'}>
                                {vista === 'etiquetas'
                                    ? etiquetasArchivadas.map((t) => (
                                          <div key={t.id} role="listitem" className="rv-cat-archivada">
                                              <span className="rv-cat-punto rv-cat-punto--apagado" style={{ background: t.color ?? 'var(--rv-ink)' }} aria-hidden="true" />
                                              <span className="rv-cat-archivada__texto">
                                                  <strong>{t.name}</strong>
                                                  <span>{[`Archivada el ${diaMes(t.archived_at)}`, (t.customer_count ?? 0) > 0 ? ((t.customer_count ?? 0) === 1 ? 'la conserva 1 cliente' : `la conservan ${String(t.customer_count)} clientes`) : ''].filter((x) => x !== '').join(' · ')}</span>
                                              </span>
                                              {editar && (
                                                  <Boton tipo="secundario" icono="archive-restore" aria-label={`Recuperar la etiqueta ${t.name}`} onClick={() => { recuperar('etiqueta', t.id, t.name); }}>
                                                      Recuperar
                                                  </Boton>
                                              )}
                                          </div>
                                      ))
                                    : camposArchivados.map((c) => (
                                          <div key={c.id} role="listitem" className="rv-cat-archivada">
                                              <span className="rv-ca-baldosa rv-cat-tipo rv-cat-tipo--apagado" aria-hidden="true">
                                                  <Icono nombre={TIPO[c.type].icono} />
                                              </span>
                                              <span className="rv-cat-archivada__texto">
                                                  <strong>{c.label}</strong>
                                                  <span>{[`${TIPO[c.type].nombre} · archivado el ${diaMes(c.archived_at)}`, c.has_values === true ? 'con valores guardados' : ''].filter((x) => x !== '').join(' · ')}</span>
                                              </span>
                                              {editar && (
                                                  <Boton tipo="secundario" icono="archive-restore" aria-label={`Recuperar el campo ${c.label}`} onClick={() => { recuperar('campo', c.id, c.label); }}>
                                                      Recuperar
                                                  </Boton>
                                              )}
                                          </div>
                                      ))}
                            </div>
                            <p className="rv-ca-nota rv-ca-nota--gris">
                                <Icono nombre="info" />
                                <span>{vista === 'etiquetas' ? TEXTOS.archivadasNota : TEXTOS.archivadosNota}</span>
                            </p>
                        </>
                    )}
                </div>
            )}

            <PanelEtiqueta
                ops={ops}
                negocio={negocio}
                movil={movil}
                editable={editar}
                abierto={abierto?.tipo === 'etiqueta' ? abierto : null}
                activas={activas}
                versiones={versiones}
                onCerrar={() => { setAbierto(null); }}
                onGuardada={(id, version) => {
                    apuntar(id, version);
                    setAbierto(null);
                    recargar();
                }}
                onArchivar={(t) => { archivar('etiqueta', t.id, t.name); }}
            />
            <PanelCampo
                ops={ops}
                negocio={negocio}
                movil={movil}
                editable={editar}
                abierto={abierto?.tipo === 'campo' ? abierto : null}
                activos={activos}
                todos={datos.campos}
                versiones={versiones}
                onCerrar={() => { setAbierto(null); }}
                onGuardado={(id, version) => {
                    apuntar(id, version);
                    setAbierto(null);
                    recargar();
                }}
                onArchivar={(c) => { archivar('campo', c.id, c.label); }}
            />
        </div>
    );
}

/** Todas las etiquetas, también las archivadas (la lista va por cursor; un catálogo cabe en pocas páginas). */
async function todasLasEtiquetas(ops: Operaciones, negocio: string): Promise<EtiquetaCatalogo[]> {
    const todas: EtiquetaCatalogo[] = [];
    let cursor: string | null = null;
    for (let pagina = 0; pagina < 20; pagina += 1) {
        const l = await ops.listTags(negocio, { include_archived: true, limit: 100, cursor });
        todas.push(...l.data);
        cursor = l.next_cursor;
        if (cursor === null) {
            break;
        }
    }
    return todas;
}
